import EmptyTableMessage from "@/components/EmptyTableMessage";
import { DataTable } from "@/components/ui/data-table";
import { TablePagination } from "@/components/ui/table-pagination";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useRequestPrompt } from "@/queries";
import { useParams } from "react-router-dom";

import {
  usePromptRequestData,
  useSortingHandler,
  useUrlParamsAndSorting,
} from "./hooks";
import { PromptTemplateVersionRequestsTable } from "./PromptTemplateVersionRequestsTable";
import { PromptTemplateVersionStats } from "./PromptTemplateVersionStats";

const PromptTemplateAnalytics = () => {
  const { promptId, versionId } = useParams();
  const workspaceId = useUser()?.activeWorkspaceId;
  const userToken = useAuth()?.userToken;

  const { page, pageSize, sorting, updateUrlParams } = useUrlParamsAndSorting();

  const requestsForPrompt = useRequestPrompt(
    promptId!,
    workspaceId!,
    versionId || "",
    userToken || "",
    pageSize,
    page,
    sorting[0]?.id,
    sorting[0]?.desc ? "desc" : "asc",
  );

  const data = usePromptRequestData(requestsForPrompt, pageSize);

  const handleSetSorting = useSortingHandler(
    page,
    pageSize,
    sorting,
    updateUrlParams,
  );

  const table = PromptTemplateVersionRequestsTable({
    data,
    setSorting: handleSetSorting,
    sorting,
  });

  return (
    <div className="flex h-full flex-col gap-y-2 overflow-x-hidden pt-2">
      <PromptTemplateVersionStats
        promptId={promptId!}
        versionNumber={parseInt(versionId!) || 0}
        workspaceId={workspaceId!}
      />
      <div className="flex min-h-0 flex-1 flex-col space-y-2 overflow-auto">
        <div className="flex-1 overflow-auto">
          <DataTable
            fullHeight
            linkTo={(row) =>
              `/workspace/${workspaceId}/request/${row.original.id}`
            }
            table={table}
            emptyMessage={<EmptyTableMessage />}
            isLoading={requestsForPrompt.isLoading}
          />
        </div>

        <div className="flex flex-shrink-0 justify-between">
          <TablePagination
            table={table}
            hasNext={data.has_next}
            hasPrev={data.has_prev}
            pages={data.pages}
            page={data.page}
            pageSize={data.per_page || pageSize}
            pageSizeOptions={[5, 10, 20, 30, 40, 50]}
            setPageIndex={(newPage) =>
              updateUrlParams(newPage, pageSize, sorting)
            }
            setPageSize={(newPageSize) =>
              updateUrlParams(1, newPageSize, sorting)
            }
          />
        </div>
      </div>
    </div>
  );
};

export default PromptTemplateAnalytics;
